import { createSelector } from 'reselect';
import { defaultTo, prop } from 'ramda';
import Signup from '@/api/user-service/signUp';
import Login from '@/api/user-service/login';
import PasswordResetRequest from '@/api/user-service/passwordResetRequest';
import PasswordResetConfirm from '@/api/user-service/passwordResetConfirm';
import { USER_ACTION as ACTIONS } from '../actions';
import { loginPayloadProps, signUpPayloadProps, passwordResetRequestPayloadProps, passwordResetConfirmPayloadProps } from '@/api/types';
import { Dispatch } from 'redux';


const savedUser = localStorage.getItem('user');
const parsedUser = savedUser ? JSON.parse(savedUser) : null;

const serializeError = (error: unknown) => {
  if (!error) return null;
  if (typeof error === 'string') return error;

  const err = error as {
    message?: string;
    name?: string;
    code?: string;
    response?: { status?: number; data?: unknown };
  };

  return {
    message: err.message ?? 'Something went wrong',
    name: err.name,
    code: err.code,
    status: err.response?.status,
    data: err.response?.data,
  };
};

const initialState = {
  email: parsedUser?.email || '',
  userName: parsedUser ? `${parsedUser.firstName} ${parsedUser.lastName}` : '',
  isAuthenticated: !!localStorage.getItem('access_token'),
  error: null,
  loading: false,
  result: '',
  userData: {
    emailid: '', 
    password: '',
  },
  formData: {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    isTermConditions: false,
  },
  resetFormData: {
    email: '',
    reset_code: '',
    new_password: '',
    confirm_password: '',
  },
  user: '',
};

const getSlice = prop('user');

// Selectors
export const getError = createSelector(getSlice, prop('error'));
export const isLoading = createSelector(getSlice, prop('loading'));
export const getEmail = createSelector(getSlice, prop('email'));
export const getUserName = createSelector(getSlice, prop('userName')); // Updated selector for emailid
export const getUserData = createSelector(getSlice, prop('userData'));
export const getFormData = createSelector(getSlice, prop('formData'));
export const getResetFormData = createSelector(getSlice, prop('resetFormData'));
export const getIsAuthenticated = createSelector(getSlice, prop('isAuthenticated'));

// SignUp Action
export const signUpUser =
  (payload: signUpPayloadProps)=> async (dispatch: Dispatch) => {
    dispatch({
      type: ACTIONS.SIGNIN_REQUEST,
    });
    try {
      const data = await Signup(payload);
      dispatch({
        type: ACTIONS.SIGNIN_SUCCESS,
        signInData: data,
      });
     
    } catch (error) {
      dispatch({
        type: ACTIONS.SIGNIN_FAILURE,
        error: error,
      });
    }
  };

// Login Action
export const loginUser =
  (payload: loginPayloadProps, navigate: (arg0: string, options?: { replace: boolean }) => void) =>
  async (dispatch: Dispatch) => {
    dispatch({
      type: ACTIONS.LOGIN_REQUEST,
    });
    try {
      const response = await Login(payload);
      const { tokens, user } = response;

      localStorage.setItem('access_token', tokens?.access);
      localStorage.setItem('refresh_token', tokens?.refresh);
      localStorage.setItem('user', JSON.stringify(user));

      const userName = `${prop('firstName', user)} ${prop('lastName', user)}`;
      const email = prop('email', user);

      dispatch({
          type: ACTIONS.LOGIN_SUCCESS,
          loggedInData: response,
      });
      dispatch({
          type: ACTIONS.USER_LOGGED_IN,
          email,
          userName,
          value: true,
      });
      navigate('/');
    } catch (error) {
      dispatch({
        type: ACTIONS.LOGIN_FAILURE,
        error: error,
      });
    }
  };

// Logout Action
export const userLogout = (navigate: (arg0: string) => void) => (dispatch: Dispatch) => {
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');

  try {
    dispatch({
      type: ACTIONS.USER_LOGGED_OUT,
      value: false,
    });
  } catch {
    // RTK immutability checks throw if a prior non-serializable value (e.g. a DOM
    // event left in app.error) was mutated between dispatches. Still finish logout.
    window.location.assign('/login');
    return;
  }

  navigate('/login');
};

// Set Form Data Action
export const setFormData = (payload: signUpPayloadProps) => (dispatch: Dispatch) => {
  const updatedFormData = { ...payload };
  dispatch({
    type: ACTIONS.SET_FORM_DATA,
    value: defaultTo('', updatedFormData),
  });
};

// Set User Data Action
export const setUserData = (payload: loginPayloadProps) => (dispatch: Dispatch) => {
  const updatedUserData = { ...payload };
  dispatch({
    type: ACTIONS.SET_USER_DATA,
    value: defaultTo('', updatedUserData),
  });
};

// Password Reset Request Action
export const requestPasswordReset =
  (payload: passwordResetRequestPayloadProps) => async (dispatch: Dispatch) => {
    dispatch({
      type: ACTIONS.PASSWORD_RESET_REQUEST,
    });
    try {
      const data = await PasswordResetRequest(payload);
      dispatch({
        type: ACTIONS.PASSWORD_RESET_SUCCESS,
        resetData: data,
      });
    } catch (error) {
      dispatch({
        type: ACTIONS.PASSWORD_RESET_FAILURE,
        error: error,
      });
    }
  };

// Password Reset Confirm Action
export const confirmPasswordReset =
  (payload: passwordResetConfirmPayloadProps, navigate: (arg0: string) => void) =>
  async (dispatch: Dispatch) => {
    dispatch({
      type: ACTIONS.PASSWORD_RESET_CONFIRM_REQUEST,
    });
    try {
      const data = await PasswordResetConfirm(payload);
      dispatch({
        type: ACTIONS.PASSWORD_RESET_CONFIRM_SUCCESS,
        resetConfirmData: data,
      });
      // Navigate to login after successful password reset
      navigate('/login');
    } catch (error) {
      dispatch({
        type: ACTIONS.PASSWORD_RESET_CONFIRM_FAILURE,
        error: error,
      });
    }
  };

// Set Reset Form Data Action
export const setResetFormData = (payload: passwordResetConfirmPayloadProps) => (dispatch: Dispatch) => {
  const updatedResetFormData = { ...payload };
  dispatch({
    type: ACTIONS.SET_RESET_FORM_DATA,
    value: defaultTo('', updatedResetFormData),
  });
};

// Reducer
export default (
  state = initialState,
  action: {
    [x: string]: unknown;
    type?: string;
  } = {}
) => {
  switch (action.type) {
    case ACTIONS.SIGNIN_REQUEST:
      return {
        ...state,
        error: null,
        loading: true,
      };
    case ACTIONS.SIGNIN_SUCCESS:
      return {
        ...state,
        error: null,
        loading: false,
        result: (action as { signInData: string }).signInData,
      };
    case ACTIONS.SIGNIN_FAILURE:
      return {
        ...state,
        error: serializeError(action.error),
        loading: false,
      };

    case ACTIONS.LOGIN_REQUEST:
      return {
        ...state,
        error: null,
        loading: true,
      };
    case ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        error: null,
        loading: false,
        result: (action as { loggedInData: string }).loggedInData,
      };
    case ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        error: serializeError(action.error),
        loading: false,
      };

    case ACTIONS.USER_LOGGED_IN:
      return {
        ...state,
        isAuthenticated: action.value,
        email: action.email,
        // emailid: action.email, // Updated to use emailid
        userName: action.userName,
      };

    case ACTIONS.USER_LOGGED_OUT:
      return {
        ...state,
        isAuthenticated: action.value,
        email: '',
        userName: '', // Reset emailid
      };

    case ACTIONS.SET_FORM_DATA:
      return {
        ...state,
        formData: action.value,
      };

    case ACTIONS.SET_USER_DATA:
      return {
        ...state,
        userData: action.value,
      };

    case ACTIONS.PASSWORD_RESET_REQUEST:
      return {
        ...state,
        error: null,
        loading: true,
      };
    case ACTIONS.PASSWORD_RESET_SUCCESS:
      return {
        ...state,
        error: null,
        loading: false,
        result: (action as { resetData: string }).resetData,
      };
    case ACTIONS.PASSWORD_RESET_FAILURE:
      return {
        ...state,
        error: serializeError(action.error),
        loading: false,
      };

    case ACTIONS.PASSWORD_RESET_CONFIRM_REQUEST:
      return {
        ...state,
        error: null,
        loading: true,
      };
    case ACTIONS.PASSWORD_RESET_CONFIRM_SUCCESS:
      return {
        ...state,
        error: null,
        loading: false,
        result: (action as { resetConfirmData: string }).resetConfirmData,
      };
    case ACTIONS.PASSWORD_RESET_CONFIRM_FAILURE:
      return {
        ...state,
        error: serializeError(action.error),
        loading: false,
      };

    case ACTIONS.SET_RESET_FORM_DATA:
      return {
        ...state,
        resetFormData: action.value,
      };

    default:
      return state;
  }
};
